<template lang="pug">
    .account.not-found(v-if="notFound")
        Error(:message="$t('Account Not Found')")
    .account.not-found(v-else-if="pathNotValid")
        Error(:message="$t('URL Not Valid')")
    .account.not-found(v-else-if="error")
        Error(:message="$t('Something went wrong!')" :code="501")
    .account(v-else)
        tabs(:items="tabs" :active-tab="activeTab" @click="activateTab")
            template(slot="tabbar-buttons")
                el-button.delete-button(
                    circle v-if="! loading && isDeletable && isSuperUser"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    :loading="deleting"
                    :disabled="isDisabledSubmit && ! deleting"
                    @click="deleteCurrentAccount"
                )
                el-button(
                    v-if="isSuperUser"
                    circle icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                )
                el-button(
                    circle v-if="!loading && isSuperUser"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    :loading="savingAndClose"
                    @click="submit($event, listRoute)"
                )
                el-button(
                    circle v-if="! loading"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :disabled="isDisabledSubmit && ! saving"
                    :loading="saving"
                    @click="submit"
                )

        el-form(label-position="top" :model="form" :rules="rules" ref="form")
            all-input-fields
            el-card(shadow="always" v-loading="loading")
                router-view.account-form(:key="$route.path" :class="{'loading-panel': loading}")
            .form-buttons
                el-button(
                    round v-if="!loading && isSuperUser"
                    icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                ) {{ $t('Dismiss') }}
                el-button(
                    round v-if="!loading && isSuperUser"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="savingAndClose"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    @click="submit($event, listRoute)"
                ) {{ $t('Save and close') }}
                el-button(
                    round v-if="! loading"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="isDisabledSubmit && ! saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Account dependencies could not be fetched.": "Benutzerabhängigkeiten konnten nicht abgerufen werden.",
        "Account could not be fetched.": "Benutzer konnte nicht gefunden werden.",
        "Account could not be deleted.": "Benutzer konnte nicht gelöscht werden.",
        "Account deleted successfully!": "Benutzer erfolgreich gelöscht!",
        "Account could not be saved.": "Benutzer konnte nicht gespeichert werden.",
        "Account saved successfully!": "Benutzer erfolgreich gespeichert!",
        "Account Not Found": "Benutzer nicht gefunden",
        "Account": "Account",
        "Addresses": "Adressen",
        "Websites": "Webauftritte",
        "Team": "Team"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { mapMutations, mapActions, mapGetters } from 'vuex';

import store from '@/store';
import router from '@/router';

import Tabs from '@/components/Tabs';
import Error from '@/components/Error';

// validate all fields, regardless if the corresponding component is mounted or not
import AllInputFields from './AllInputFields';

export default {
    name: 'AccountForm',
    data() {
        return {
            listRoute: { name: 'system-accounts' },
            error: false,
            notFound: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            savingAndClose: false,
            deleting: false,
            prePath: '/account',

            activeTab: undefined,
        };
    },
    components: {
        Tabs,
        Error,
        AllInputFields
    },
    watch: {
        '$route.fullPath'() {
            this.activeTab = this.$route.fullPath
        }
    },
    computed: {
        id() {
            return _.get(this.form, 'id', false);
        },
        isDeletable() {
            return !!this.id && this.id !== _.get(this.user, 'id');
        },
        isDisabledSubmit() {
            return this.saving || this.savingAndClose || this.deleting;
        },
        tabs() {

            let name = this.$t('Account');
            const firstName = _.get(this.form, 'first_name');
            const lastName = _.get(this.form, 'last_name');

            if (firstName || lastName) {
                name = _.trim(`${firstName} ${lastName}`);
            }

            let tabs = [
                {
                    name,
                    path: this.prePath + '/main',
                    icon: {
                        name: 'user-circle',
                        group: 'fas'
                    }
                },
                {
                    name: this.$t('Addresses'),
                    path: this.prePath + '/addresses',
                    icon: {
                        name: 'map-marker-alt',
                        group: 'fas'
                    }
                }
            ];

            if (this.options.cms.items.length)
                tabs.push(
                    {
                        name: this.$t('Team'),
                        path: this.prePath + '/team',
                        icon: {
                            name: 'users',
                            group: 'fas'
                        }
                    }
                )
            return tabs;
        },
        ...mapGetters({
            form: 'account/form',
            initialized: 'account/form_initialized',
            validateOnEnter: 'account/form_validateOnEnter',
            rules: 'account/rules',

            user: 'auth/user',
            isSuperUser: 'auth/isSuperUser',

            // shop
            activeCms: 'activeCms',

            options: 'account/options'
        })
    },
    methods: {
        initialize(id = null) {
            if (id === null) id = this.$route.params.id

            if (this.initialized !== true) {

                this.setInitializedFlag(true);

                if (id !== undefined) {
                    this.prePath = '/account/' + id
                } else {
                    this.prePath = '/account'
                }

                let actions = [];

                const fetchDependencies = () => {
                    if (this.id) {
                        actions.push(this.fetchCmsOptions(this.id))

                        // set team_initiator filter for team member list
                        // const filter = {
                        //     key: 'initiator',
                        //     match: this.id
                        // };
                        // this.defineTeamMemberFilterValue({ key: 'initiator', value: filter });
                        this.defineTeamMemberPaginationSizes([5, 10, 50]);
                        this.defineTeamMemberItemsPerPage(10);
                        actions.push(
                            this.fetchTeamMembersPaginated({ initiator: this.id, scope: 'base', type: 'administrator_team' }).finally(() => {
                                this.updateTeamMembersLoading(false);
                            })
                        );
                    }

                    return Promise.all(actions);
                };

                this.error = false;
                this.loading = true;

                if (id === 'new') {

                    fetchDependencies()
                        .then(() => {
                            this.loading = false;
                            store.dispatch('account/reset');
                        })
                        .catch(err => {
                            this.loading = false;
                            this.error = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Account dependencies could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                }
                else if (!isNaN(id) || id === undefined) {

                    let accountID;
                    if (id === undefined) {
                        accountID = this.user.id
                    }
                    else {
                        accountID = id
                    }

                    this.fetchAccount(accountID)
                        .then(() => {
                            this.loading = false;
                            this.notFound = false;

                            // fetch options for dropdowns
                            fetchDependencies()
                                .then(() => {
                                    store.commit('account/resetRules');
                                })
                                .catch(err => {
                                    this.error = true;

                                    /* eslint no-console: ["error", { allow: ["error"] }] */
                                    console.error(err);

                                    this.$notify.error({
                                        title: this.$t('An error occurred!'),
                                        message: this.$t('Account dependencies could not be fetched.'),
                                        iconClass: 'fas el-icon-fa-xmark',
                                        position: 'bottom-left',
                                        customClass: 'dark',
                                        duration: 8000
                                    });
                                });
                        })
                        .catch(err => {
                            this.loading = true;
                            this.notFound = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Account could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.loading = true;
                    this.pathNotValid = true;
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // do nothing
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        deleteCurrentAccount() {
            this.deleting = true;
            this.delete(this.id)
                .then(() => {

                    router.push(this.listRoute);

                    this.$notify.success({
                        title: this.$t('Account deleted successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.deleting = false;

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Account could not be deleted.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                });
        },
        submit(event, redirect = false) {
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    if (redirect) this.savingAndClose = true;
                    else this.saving = true;

                    let save = this.create;
                    if (this.id) save = this.update;

                    let payload = _.cloneDeep(this.form);

                    save(payload)
                        .then(user => {
                            this.$nextTick(() => {
                                store
                                    .dispatch('fetchSetup')
                                    .then(() => {
                                        this.$notify.success({
                                            title: this.$t('Account saved successfully!'),
                                            iconClass: 'fas el-icon-fa-check',
                                            position: 'bottom-left',
                                            customClass: 'dark',
                                            duration: 8000
                                        });

                                        if (redirect) {
                                            router.push(redirect);
                                        } else {
                                            if (this.$route.params.id === 'new') {
                                                const route = router.currentRoute
                                                route.params.id = user.id
                                                router.replace(route)
                                            }
                                        }
                                    })
                                    .finally(() => {
                                        if (redirect) this.savingAndClose = false;
                                        else this.saving = false;
                                    });
                            });
                        })
                        .catch(err => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */

                            if (redirect) this.savingAndClose = false;
                            else this.saving = false;

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Account could not be saved.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.setValidateOnEnterFlag(true);

                    // check the invalid fields
                    // and go to the corresponding tab
                    // or open the corresponding closed area
                    // TODO: scroll to invalid field

                    let invalidTabs = [];
                    _.each(errors, (error, path) => {
                        if (_.indexOf(['gender', 'first_name', 'last_name', 'email', 'password'], path) >= 0) {
                            invalidTabs.push(this.prePath + '/main');
                        }
                        if (_.startsWith(path, 'addresses.')) {
                            invalidTabs.push(this.prePath + '/addresses');
                        }
                    });

                    invalidTabs = _.uniq(invalidTabs);

                    // check if the current tab
                    // is present in invalidTabs
                    // if not, go to next (first) invalid tab

                    const match = _.indexOf(invalidTabs, this.$route.fullPath);

                    let switchTab = false;
                    if (match < 0) {
                        invalidTabs.every(invalidTab => {
                            if (invalidTab && this.activeTab !== invalidTab) {
                                const tab = _.find(this.tabs, { path: invalidTab });
                                if (tab) {
                                    this.activateTab(tab);
                                    switchTab = true;

                                    // break loop after the first tab has been activated
                                    return false;
                                }
                            }
                        });
                    }

                    // // triger the validation again
                    // // so the previously hidden fields will get the errors
                    // // ---
                    // // does not work if the component has been destroyed
                    // // in the meantime (f.a. by switching tabs)
                    // if (switchTab === false) {
                    //     setTimeout(() => {
                    //         this.$refs.form.validate(() => {
                    //             // do nothing
                    //             // callback function is required :(
                    //         })
                    //     }, 100);
                    // }

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        leave() {
            router.push(this.listRoute);
        },
        ...mapMutations({
            setInitializedFlag: 'account/form_initialized',
            setValidateOnEnterFlag: 'account/form_validateOnEnter',

            updateTeamMembersLoading: 'teamMember/updatePaginationLoading',
            defineTeamMemberFilterValue: 'teamMember/defineFilterValue',
            defineTeamMemberPaginationSizes: 'teamMember/definePaginationSizes',
            defineTeamMemberItemsPerPage: 'teamMember/defineItemsPerPage'
        }),
        ...mapActions({
            activateTab: 'activateTab',
            fetchAccount: 'account/fetchAccount',
            // fetchTemplateOptions: 'account/fetchTemplateOptions',
            fetchCmsOptions: 'account/fetchCmsOptions',

            fetchTeamMembersPaginated: 'teamMember/fetchTeamMembersPaginated',

            create: 'account/create',
            update: 'account/update',
            delete: 'account/delete'
            // fetchAccountsPaginated: 'account/fetchAccountsPaginated',
        })
    },
    mounted() {
        this.activeTab = this.$route.fullPath
        this.initialize();
    },
    beforeRouteEnter(to, from, next) {

        const id = _.get(to, 'params.id');

        if (to.name === 'system-account-form') {
            router.push({ name: 'system-account-main', params: { id } })
        } else {
            next();
        }
    },
    beforeRouteUpdate(to, from, next) {
        if (this.initialized !== true) {
            const id = _.get(to, 'params.id');
            if (to.name === 'system-account-form') {
                next({ name: 'system-account-main', params: { id } });
            } else {
                next();
            }
            this.$nextTick(() => {
                this.initialize(id)
            })
        } else {
            next()
        }
    },
    beforeRouteLeave(to, from, next) {
        next();

        // TODO: add reset action
        // see event form

        // issue: after saving an account, the life cycle methods
        // destroyed, beforeRouteLeave, etc. are gettings triggered

        // workaround
        // check if there is the route /account/:id
        // in the to.matched collection

        const leave = !to.matched.find(route => route.path === '/account/:id');
        if (leave) {
            store.dispatch('account/reset');
            store.commit('account/resetUI');
            this.$nextTick(() => store.commit('account/resetRules'));
        }
    }
};
</script>

<style lang="sass" scoped>
.account
    :deep()
        .el-card__body
            padding: 0
            & > section
                padding: 20px
</style>
